import React, { useState, useEffect, useCallback, useContext } from "react";
import "./BauerPivotItem.scss";
import { useNavigate, useParams } from "react-router";
import { IconButton, Slide, Tooltip } from "@material-ui/core";
import MobileZone from "../../../../../../../../components/Zones/MobileZone";
import DesktopZone from "../../../../../../../../components/Zones/DesktopZone";
import CircularProgress from "@material-ui/core/CircularProgress";
import { Done, Autorenew, Opacity } from "@material-ui/icons";
import { DrawerContext } from "../../../../../../components/DrawerProvider/DrawerProvider";
import SignalWifi3BarIcon from "@material-ui/icons/SignalWifi3Bar";
import SignalWifiOffIcon from "@material-ui/icons/SignalWifiOff";
import moment from "moment-timezone";
import styled from "@emotion/styled";
import { i18n } from "../../../../../../../../i18n/i18nText";
import routes from "../../../../../../../../routes/routes";
import {
  BauerPivot,
  FarmBauerPivotObj,
} from "../../../../../../../../redux/bauerPivot/types";
import {
  bauerPivotStatus,
  colorStatus,
} from "../../../../../../../../utils/models/bauerpivots";
import useSocketIO from "../../../../../../../../hooks/tools/useSocketIO";

const InformationGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 5fr;
  grid-gap: 5px;
`;

const Status = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  max-width: 180px;
  background-color: gray;
  font-weight: 500;
  padding: 4px 8px;
  font-size: 0.7rem;
  border-radius: 8px;
  color: white;

  @media (max-width: 1024px) {
    margin-top: 12px;
  }
`;

const { SOCKET_SUFFIX } = process.env;

const SpanStatus = styled.span``;

interface Props {
  bauerPivot: FarmBauerPivotObj;
  sweepStatus?: number; // 0 - sweep not activated, 1 - sweep started, 2 - sweep done successfully
  shouldNotShowMapPreview?: boolean;
  shouldHideLatestStatus?: boolean;
  shouldHideInfoFromRecoil?: boolean;
}

function BauerPivotItem(props: Props) {
  const { bauerPivot } = props;
  const [isConnected, socket] = useSocketIO();
  const [currentBauerPivot, setBauerPivot] = useState({ ...bauerPivot });

  useEffect(() => {
    let machine_address = "";
    (async () => {
      machine_address = bauerPivot.hardware_id;
      if (isConnected) {
        socket.subscribe(`${machine_address}@smartrain_pivot`);
        socket.bind("message_up_received", (data: BauerPivot) => {
          setBauerPivot({
            ...bauerPivot,
            latest: {
              ...bauerPivot.latest,
              status: data.status,
              zero_position: data.zero_position,
              direction: data.direction,
              sector_begin: data.sector_begin,
              sector_end: data.sector_end,
              machine_angle: data.machine_angle,
              last_tower_radius: data.last_tower_radius,
              id: data.id,
              created_at: data.created_at,
            },
            metadata: {
              ...bauerPivot.metadata,
              last_communication: data.created_at,
            },
          });
        });
      }
    })();
    return () => {
      if (isConnected) {
        socket.unbind("message_up_received");
        socket.unsubscribe(`${machine_address}@smartrain_pivot`);
      }
    };
  }, []);

  const isOnlineIndicator = () => {
    return (
      <IconButton color="primary">
        {bauerPivot.connected ? (
          <Tooltip title={i18n("SELECTED_FARM_PIVOT_ONLINE")}>
            <SignalWifi3BarIcon style={{ color: "#037ade" }} />
          </Tooltip>
        ) : (
          <Tooltip title={i18n("SELECTED_FARM_PIVOT_OFFLINE")}>
            <SignalWifiOffIcon style={{ color: "gray", opacity: "0.6" }} />
          </Tooltip>
        )}
      </IconButton>
    );
  };

  const farmID: string = useParams().farm;

  const navigate = useNavigate();

  const [runningSweep, setRunningSweep] = useState<number>(undefined);

  const ctx = useContext(DrawerContext);

  const pivotItemHandler = useCallback(() => {
    ctx.setFarmID(farmID);

    ctx.setPivotID(String(currentBauerPivot.id));
    navigate(
      routes.DASHBOARD.SELECTED_BAUER_PIVOT.replace(":farm", farmID).replace(
        ":pivot",
        String(currentBauerPivot.id)
      )
    );
  }, [farmID, currentBauerPivot.id]);

  useEffect(() => {
    if (props.sweepStatus !== undefined) {
      setRunningSweep(props.sweepStatus);
    }
  }, [props.sweepStatus]);

  return (
    <div className="pivot-item__content">
      <DesktopZone>
        <div className="pivot-item" onClick={pivotItemHandler}>
          <div className="pivot-item__row-left">
            <div className="pivot-item__text-container">
              <div className="pivot-item__text-content">
                <div className="pivot-item__title">
                  <h4>{currentBauerPivot.name}</h4>
                  {currentBauerPivot.latest?.status != undefined ? (
                    <Tooltip
                      title={bauerPivotStatus(
                        currentBauerPivot.latest.status
                      ).toUpperCase()}
                      aria-label="info"
                    >
                      <Status
                        style={{
                          backgroundColor: colorStatus(
                            currentBauerPivot?.latest.status
                          ),
                        }}
                        className="pivot-item__status"
                      >
                        <span>
                          {bauerPivotStatus(currentBauerPivot.latest.status)
                            .length > 20
                            ? bauerPivotStatus(currentBauerPivot.latest.status)
                                .slice(0, 20)
                                .toUpperCase() + "..."
                            : bauerPivotStatus(
                                currentBauerPivot.latest.status
                              ).toUpperCase()}
                        </span>
                      </Status>
                    </Tooltip>
                  ) : (
                    <Tooltip
                      title={bauerPivotStatus(
                        currentBauerPivot.latest.status
                      ).toUpperCase()}
                      aria-label="info"
                    >
                      <Status
                        style={{
                          backgroundColor: "#757575",
                        }}
                        className="pivot-item__status"
                      >
                        <span>{bauerPivotStatus(-1).toUpperCase()}</span>
                      </Status>
                    </Tooltip>
                  )}
                </div>
                {currentBauerPivot.latest.machine_angle >= 0 ? (
                  <div className="pivot-item__angle">
                    <Autorenew style={{ marginLeft: "-5px" }} />
                    {`${Math.round(currentBauerPivot.latest.machine_angle)}º`}
                  </div>
                ) : (
                  <div className="pivot-item__angle">
                    <Autorenew style={{ marginLeft: "-5px" }} />
                    {"-"}
                  </div>
                )}

                <div className="pivot-item__last-update">
                  {currentBauerPivot.metadata?.last_communication ? (
                    moment
                      .utc(currentBauerPivot.metadata.last_communication)
                      .tz(moment.tz.guess())
                      .format("DD MMM HH:mm")
                  ) : (
                    <>{"-"}</>
                  )}
                  {isOnlineIndicator()}
                </div>
              </div>
            </div>
          </div>

          <div className="pivot-item__row-right">
            <div className="pivot-item__sweep-status">
              {runningSweep === 1 ? <CircularProgress /> : <></>}

              {runningSweep === 2 ? <Done /> : <></>}
            </div>
          </div>
        </div>
      </DesktopZone>

      <MobileZone>
        <Slide direction="down" in>
          <div className="pivot-item" onClick={pivotItemHandler}>
            <div className="pivot-item__row-left">
              <div className="pivot-item__text-container">
                <div className="pivot-item__text-content">
                  <div className="pivot-item__title">
                    <h4>{currentBauerPivot.name}</h4>
                    {currentBauerPivot.latest?.status != undefined ? (
                      <Tooltip
                        title={bauerPivotStatus(
                          currentBauerPivot.latest.status
                        ).toUpperCase()}
                        aria-label="info"
                      >
                        <Status
                          style={{
                            backgroundColor: colorStatus(
                              currentBauerPivot?.latest.status
                            ),
                          }}
                          className="pivot-item__status"
                        >
                          <span>
                            {bauerPivotStatus(currentBauerPivot.latest.status)
                              .length > 20
                              ? bauerPivotStatus(
                                  currentBauerPivot.latest.status
                                )
                                  .slice(0, 20)
                                  .toUpperCase() + "..."
                              : bauerPivotStatus(
                                  currentBauerPivot.latest.status
                                ).toUpperCase()}
                          </span>
                        </Status>
                      </Tooltip>
                    ) : (
                      <Tooltip
                        title={bauerPivotStatus(-1).toUpperCase()}
                        aria-label="info"
                      >
                        <Status
                          style={{
                            backgroundColor: "#757575",
                          }}
                          className="pivot-item__status"
                        >
                          <span>{bauerPivotStatus(-1)}</span>
                        </Status>
                      </Tooltip>
                    )}
                  </div>
                  <InformationGrid id="InformationGrid">
                    {`${currentBauerPivot.latest.machine_angle}` != "" ? (
                      <div className="pivot-item__angle">
                        <Autorenew style={{ marginLeft: "-5px" }} />
                        {currentBauerPivot.latest.machine_angle
                          ? `${Math.round(
                              currentBauerPivot.latest.machine_angle
                            )}º`
                          : "-"}
                      </div>
                    ) : null}
                  </InformationGrid>
                  <div className="pivot-item__last-update">
                    {currentBauerPivot.metadata?.last_communication ? (
                      moment
                        .utc(currentBauerPivot.metadata.last_communication)
                        .tz(moment.tz.guess())
                        .format("DD MMM HH:mm")
                    ) : (
                      <>{"-"}</>
                    )}
                    {isOnlineIndicator()}
                  </div>
                </div>
              </div>
            </div>

            <div className="pivot-item__row-right">
              <div className="pivot-item__sweep-status">
                {runningSweep === 1 ? <CircularProgress /> : <></>}

                {runningSweep === 2 ? <Done /> : <></>}
              </div>
            </div>
          </div>
        </Slide>
      </MobileZone>
    </div>
  );
}

export default React.memo(BauerPivotItem);
